// Guatemala calendar/clock helpers for the AI assistant (executor + snapshot).
// America/Guatemala is UTC-6 all year (no DST), so "today" must never come from
// toISOString() (UTC: already tomorrow after 18:00 GT) nor from the device's
// timezone. Mirrored in mobile/utils/gt-date.ts; keep both identical.

// ponytail: fixed UTC-6 offset instead of Intl timeZone (Guatemala has had no DST
// since 2006) — no dependency on the engine's tz data (Hermes on mobile).
const GT_OFFSET_MS = 6 * 60 * 60 * 1000;

/** Guatemala wall-clock time as an ISO-shaped string 'YYYY-MM-DDTHH:mm:ss.sssZ' (Z is not real). */
function isoGT(d: Date): string {
  return new Date(d.getTime() - GT_OFFSET_MS).toISOString();
}

/** Today's date in Guatemala, 'YYYY-MM-DD'. */
export function todayGT(): string {
  return isoGT(new Date()).slice(0, 10);
}

/** Calendar arithmetic on a 'YYYY-MM-DD' date (noon UTC keeps it off day edges). */
export function addDaysGT(ymd: string, n: number): string {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** A timestamp as Guatemala local 'YYYY-MM-DD HH:mm' — the form the model reads.
 *  A bare 'YYYY-MM-DD' (a DATE column) is already a calendar day: passed through,
 *  since new Date() would read it as UTC midnight = the previous day 18:00 GT. */
export function fmtGT(v: string | Date): string {
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return String(v);
  const s = isoGT(d);
  return `${s.slice(0, 10)} ${s.slice(11, 16)}`;
}

/** 00:00 Guatemala time on `ymd`, as an ISO string. */
export function startOfDayGTISO(ymd: string): string {
  return `${ymd}T00:00:00-06:00`;
}

/** Monday..Sunday of the current Guatemala week. */
export function weekBoundsGT(): { from: string; to: string } {
  const today = todayGT();
  const dow = new Date(`${today}T12:00:00Z`).getUTCDay(); // 0 = Sunday
  const from = addDaysGT(today, -((dow + 6) % 7));
  return { from, to: addDaysGT(from, 6) };
}

/** First day of the current Guatemala month, 'YYYY-MM-01'. */
export function monthStartGT(): string {
  return `${todayGT().slice(0, 8)}01`;
}
